import { gql, TypedDocumentNode } from "@apollo/client";

import {
  newReferenceFragment,
  parentReferenceFragment,
  projectReferenceFragment,
} from "./references";
import { aggregatedTeamMemberFragment } from "./teamMembers";
import {
  AllNewProjectsQuery,
  AllNewProjectsQueryVariables,
  AllNewProjectsSearchQuery,
  AllNewProjectsSearchQueryVariables,
  EditNewProjectQuery,
  EditNewProjectQueryVariables,
  Employee,
  GetNewProjectQuery,
  GetNewProjectQueryVariables,
  NewArchiveItem,
  NewProject,
  ProjectReferencesQuery,
  ProjectReferencesQueryVariables,
  CreateProjectMutationMutation,
  CreateProjectMutationMutationVariables,
  DeleteNewProjectMutationMutation,
  DeleteNewProjectMutationMutationVariables,
  EditProjectMutationMutation,
  EditProjectMutationMutationVariables,
  RestoreProjectMutationMutation,
  RestoreProjectMutationMutationVariables,
  AllClientsQueryQuery,
  AllClientsQueryQueryVariables,
} from "generated/graphql";

// fragments

export const projectKeyPersonFragment: TypedDocumentNode<Employee> = gql`
  fragment Projects_key_person_fields on Employee {
    id
    firstName
    lastName
    activeDirectoryId
    profilePicture(size: 96)
  }
`;

export const projectFragment: TypedDocumentNode<NewArchiveItem> = gql`
  fragment ArchiveItem_Fields on NewArchiveItem {
    id
    archiveNumber
    location
    room
    shelf
    description
    internalNumber
    dateCreated
    keywords
  }

  fragment Project_fields on NewProject {
    id
    abacusProjectId
    archivedProjectId
    name {
      id
      de
      en
      fr
      it
    }
    description {
      id
      de
      en
      fr
      it
    }
    status
    enrichedFrom
    relatedProjectIds
    clientId
    client {
      id
      name
    }
    invoicingResponsible {
      id
      activeDirectoryId
      isFormerEmployee
    }
    invoicingResponsibleId
    adminProjectManagerId
    adminDeputyProjectManagerId
    portfolioManagerId
    isUnlisted
    isInternalProject
    clientHidden
    departmentId
    siaPhases
    hoaiPhases
    frPhases
    itPhases
    primaryPhase
    applicationAreas
    services
    investment
    investmentCurrency
    country
    projectFolderLink
    facts {
      id
      value {
        id
        de
        en
        fr
        it
      }
      newText
      keyId
      key {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    archiveItems {
      ...ArchiveItem_Fields
    }
    isReadyForReview
  }
`;

export const projectListFields: TypedDocumentNode<NewProject> = gql`
  fragment ProjectList_fields on NewProject {
    id
    abacusProjectId
    teamSize
    projectMainReferencesCount
    from
    to
    name {
      id
      de
      en
      fr
      it
    }
    siaPhases
    hoaiPhases
    frPhases
    itPhases
    primaryPhase
    client {
      id
      name
    }
    isUnlisted
    parentProjects {
      id
      abacusProjectId
      teamSize
      projectMainReferencesCount
      from
      to
      name {
        id
        de
        en
        fr
        it
      }
      siaPhases
      hoaiPhases
      frPhases
      itPhases
      primaryPhase
      client {
        id
        name
      }
    }
  }
`;

const projectRelationsFragment: TypedDocumentNode<NewProject> = gql`
  ${parentReferenceFragment}
  ${projectListFields}
  ${projectKeyPersonFragment}
  ${aggregatedTeamMemberFragment}

  fragment ProjectRelations_fields on NewProject {
    adminProjectManager {
      ...Projects_key_person_fields
    }
    adminDeputyProjectManager {
      ...Projects_key_person_fields
    }
    portfolioManager {
      ...Projects_key_person_fields
    }
    aggregatedTeam(includeUnapproved: true) {
      ...aggregatedTeamMemberFragment
      references(includeUnapproved: true) {
        ...NewReference_fields
        parentReference {
          ...ParentReference_fields
        }
        draft {
          ...NewReference_fields
          parentReference {
            ...ParentReference_fields
          }
        }
      }
      employee {
        id
        firstName
        lastName
        fullName
        activeDirectoryId
        isFormerEmployee
        profilePicture(size: 96)
      }
    }
    projectReferences(includeUnapproved: true) {
      ...NewReference_fields
      parentReference {
        ...ParentReference_fields
      }
      isStarredForCurrentUser
      draft {
        ...NewReference_fields
        parentReference {
          ...ParentReference_fields
        }
      }
    }
    relatedProjects {
      ...ProjectList_fields
    }
  }
`;

// project

export const NEW_PROJECT_QUERY: TypedDocumentNode<
  GetNewProjectQuery,
  GetNewProjectQueryVariables
> = gql`
  ${projectFragment}
  ${projectRelationsFragment}
  ${newReferenceFragment}

  query GetNewProject($id: ID, $accountingId: ID, $archivedProjectId: ID) {
    me {
      id
    }
    newProject(
      id: $id
      accountingId: $accountingId
      archivedProjectId: $archivedProjectId
    ) {
      ...Project_fields
      deletedAt
      ...ProjectRelations_fields
      parentProjects {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
      approvedBy {
        __typename
      }
      approvedEntityId
      connectedFields {
        field
        isConnected
      }
      draft {
        ...Project_fields
        ...ProjectRelations_fields
        modifiedBy {
          __typename
        }
        deletedAt
        connectedFields {
          field
          isConnected
        }
      }
    }
  }
`;

export const EDIT_PROJECT_QUERY: TypedDocumentNode<
  EditNewProjectQuery,
  EditNewProjectQueryVariables
> = gql`
  ${projectFragment}
  ${projectReferenceFragment}

  query EditNewProject($id: ID!) {
    newProject(id: $id) {
      ...Project_fields
      abacusProject {
        name
        status
        clientId
        projectManagerId
        deputyProjectManagerId
        invoicingResponsibleId
        portfolioManagerId
        parentProjectId
      }
      projectReferences {
        ...ProjectReference_fields
      }
      approvedBy {
        __typename
      }
      connectedFields {
        field
        isConnected
        previewValue
      }
      draft {
        ...Project_fields
        projectReferences {
          ...ProjectReference_fields
        }
        modifiedBy {
          __typename
        }
        connectedFields {
          field
          isConnected
          previewValue
        }
        deletedAt
      }
    }
  }
`;

export const ALL_PROJECTS_QUERY: TypedDocumentNode<
  AllNewProjectsQuery,
  AllNewProjectsQueryVariables
> = gql`
  query AllNewProjects {
    newProjects(includeUnlisted: true) {
      id
      name {
        id
        de
        en
        fr
        it
      }
      abacusProjectId
    }
  }
`;

export const ALL_PROJECTS_SEARCH_QUERY: TypedDocumentNode<
  AllNewProjectsSearchQuery,
  AllNewProjectsSearchQueryVariables
> = gql`
  query AllNewProjectsSearch {
    newProjects {
      id
      name {
        id
        de
        en
        fr
        it
      }
      abacusProjectId
    }
  }
`;

export const PROJECT_REFERENCES_QUERY: TypedDocumentNode<
  ProjectReferencesQuery,
  ProjectReferencesQueryVariables
> = gql`
  ${projectReferenceFragment}

  query ProjectReferences($id: ID!) {
    newProject(id: $id) {
      id
      projectReferences(includeUnapproved: false) {
        ...ProjectReference_fields
      }
    }
  }
`;

export const CREATE_NEW_PROJECT_MUTATION: TypedDocumentNode<
  CreateProjectMutationMutation,
  CreateProjectMutationMutationVariables
> = gql`
  ${projectFragment}

  mutation CreateProjectMutation($fields: InputNewProject!) {
    createNewProject(fields: $fields) {
      ...Project_fields
      draft {
        ...Project_fields
      }
    }
  }
`;

export const DELETE_NEW_PROJECT_MUTATION: TypedDocumentNode<
  DeleteNewProjectMutationMutation,
  DeleteNewProjectMutationMutationVariables
> = gql`
  ${projectFragment}

  mutation DeleteNewProjectMutation($id: ID!) {
    deleteNewProject(id: $id) {
      ...Project_fields
      draft {
        ...Project_fields
        deletedAt
      }
    }
  }
`;

export const EDIT_NEW_PROJECT_MUTATION: TypedDocumentNode<
  EditProjectMutationMutation,
  EditProjectMutationMutationVariables
> = gql`
  ${projectFragment}

  mutation EditProjectMutation($id: ID!, $fields: InputNewProject!) {
    editNewProject(id: $id, fields: $fields) {
      ...Project_fields
      approvedBy {
        __typename
      }
      draft {
        ...Project_fields
        modifiedBy {
          __typename
        }
      }
    }
  }
`;

export const RESTORE_PROJECT_MUTATION: TypedDocumentNode<
  RestoreProjectMutationMutation,
  RestoreProjectMutationMutationVariables
> = gql`
  ${projectFragment}

  mutation RestoreProjectMutation($id: ID!) {
    restoreDeletedNewProject(id: $id) {
      ...Project_fields
    }
  }
`;

export const ALL_CLIENTS_QUERY: TypedDocumentNode<
  AllClientsQueryQuery,
  AllClientsQueryQueryVariables
> = gql`
  query allClientsQuery {
    clients {
      id
      name
    }
  }
`;
